/**
 * Push Notification Service
 * Handles browser push notifications for MoreMe AI PWA
 * 
 * Features:
 * - Push subscription management (subscribe/unsubscribe via backend API)
 * - Local notification dispatch via service worker messaging
 * - App badge count management (navigator.setAppBadge)
 * - Permission request handling
 * - Content-aware notification routing
 */

import apiClient from './api/config';

export interface PushNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  url?: string;
  type?: string;
  badgeCount?: number;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  data?: Record<string, unknown>;
}

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

class PushNotificationService {
  private static instance: PushNotificationService;
  private swRegistration: ServiceWorkerRegistration | null = null;

  private constructor() {
    // initialization happens lazily via init()
  }

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  // ─── Helpers ────────────────────────────────────────────────

  /** Is the Push API available in this browser? */
  public isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /** Current permission state */
  public getPermission(): PermissionState {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission as PermissionState;
  }

  /** Convenience: is permission granted? */
  public isGranted(): boolean {
    return this.getPermission() === 'granted';
  }

  // ─── Service Worker ─────────────────────────────────────────

  /** Ensure our SW is registered and ready */
  private async getRegistration(): Promise<ServiceWorkerRegistration> {
    if (this.swRegistration) return this.swRegistration;
    this.swRegistration = await navigator.serviceWorker.ready;
    return this.swRegistration;
  }

  // ─── Permission ─────────────────────────────────────────────

  /** Prompt the user for notification permission. Returns true if granted. */
  public async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) return false;
    try {
      const result = await Notification.requestPermission();
      return result === 'granted';
    } catch {
      return false;
    }
  }

  // ─── Push Subscription ─────────────────────────────────────

  /**
   * Full subscribe flow:
   * 1. Request permission (if not already granted)
   * 2. Get or create a PushSubscription via the browser Push API
   * 3. Send the subscription object to the backend
   */
  public async subscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    // Step 1 – permission
    if (!this.isGranted()) {
      const granted = await this.requestPermission();
      if (!granted) return false;
    }

    try {
      const registration = await this.getRegistration();

      // Check for existing subscription first
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // Fetch VAPID public key from backend
        const vapidKey = await this.fetchVapidKey();
        if (vapidKey) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(vapidKey),
          });
        } else {
          // No VAPID key configured – subscribe without applicationServerKey
          // (works for local / self-hosted push setups)
          console.warn('No VAPID key configured. Push subscription will work with local notifications only.');
        }
      }

      // Step 3 – send to backend
      if (subscription) {
        await this.saveSubscriptionToBackend(subscription);
      }

      return subscription !== null;
    } catch (err) {
      console.error('Push subscribe failed:', err);
      return false;
    }
  }

  /** Unsubscribe from push and remove from backend */
  public async unsubscribe(): Promise<boolean> {
    try {
      const registration = await this.getRegistration();
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await this.removeSubscriptionFromBackend(endpoint);
      }
      return true;
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
      return false;
    }
  }

  /** Check if the user currently has an active push subscription */
  public async isSubscribed(): Promise<boolean> {
    try {
      const registration = await this.getRegistration();
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch {
      return false;
    }
  }

  // ─── Local Notifications ────────────────────────────────────

  /**
   * Show a notification via the service worker (works even when tab is in background).
   * This is used for "local" notifications triggered by the app itself when it
   * detects new content (e.g. new announcement, new shout out).
   */
  public async showNotification(options: PushNotificationOptions): Promise<void> {
    if (!this.isGranted()) return;

    try {
      const registration = await this.getRegistration();

      if (registration.active) {
        // Preferred path: ask the SW to show the notification so it can also update the badge
        registration.active.postMessage({
          type: 'SHOW_NOTIFICATION',
          title: options.title,
          body: options.body,
          tag: options.tag,
          url: options.url,
          notificationType: options.type,
          badgeCount: options.badgeCount ?? 1,
        });
      } else {
        // Fallback: show directly from the registration
        await registration.showNotification(options.title, {
          body: options.body,
          icon: options.icon || '/android-chrome-192x192.png',
          badge: options.badge || '/favicon-32x32.png',
          tag: options.tag || 'moreme-' + Date.now(),
          vibrate: [200, 100, 200],
          data: { url: options.url || '/app/dashboard', type: options.type || 'general' },
        });
      }
    } catch (err) {
      console.error('showNotification failed:', err);
    }
  }

  // ─── Badge ──────────────────────────────────────────────────

  /** Set the PWA app icon badge count */
  public async setBadge(count: number): Promise<void> {
    // Update via the navigator API directly
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          await (navigator as any).setAppBadge(count);
        } else {
          await (navigator as any).clearAppBadge();
        }
      }
    } catch { /* not supported */ }

    // Also tell the SW so it can update from background
    try {
      const registration = await this.getRegistration();
      if (registration.active) {
        registration.active.postMessage({ type: 'SET_BADGE', count });
      }
    } catch { /* ignore */ }
  }

  /** Clear the badge */
  public async clearBadge(): Promise<void> {
    await this.setBadge(0);
  }

  // ─── Notification Management ────────────────────────────────

  /** Clear all displayed notifications */
  public async clearNotifications(): Promise<void> {
    try {
      const registration = await this.getRegistration();
      if (registration.active) {
        registration.active.postMessage({ type: 'CLEAR_NOTIFICATIONS' });
      }
    } catch { /* ignore */ }
  }

  // ─── Backend Communication ──────────────────────────────────

  private async fetchVapidKey(): Promise<string | null> {
    try {
      const res = await apiClient.get('/notifications/vapid-key');
      return res.data?.data?.publicKey || null;
    } catch {
      return null;
    }
  }

  private async saveSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    try {
      await apiClient.post('/notifications/push-subscribe', {
        subscription: subscription.toJSON(),
      });
    } catch (err) {
      console.error('Failed to save push subscription to backend:', err);
    }
  }

  private async removeSubscriptionFromBackend(endpoint: string): Promise<void> {
    try {
      await apiClient.delete('/notifications/push-unsubscribe', {
        data: { endpoint },
      });
    } catch (err) {
      console.error('Failed to remove push subscription from backend:', err);
    }
  }

  // ─── Utilities ──────────────────────────────────────────────

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Export singleton
export const pushNotificationService = PushNotificationService.getInstance();
export default pushNotificationService;
